import * as React from 'react';
import "./Welcome.scss";
import {
    Alert,
    Button,
    CloseIcon,
    Flex,
    FlexItem,
    Header,
    ImageLibraryIcon,
    Loader,
    MegaphoneIcon,
    SaveIcon
} from "@fluentui/react-northstar";
import EditInput from "../Utils/EditInput";
import {getInitAdaptiveCard, updateWelcomeCard} from "../AdaptiveCard/adaptiveCard";
import RichTextEditor from "../Utils/RichTextEditor";
import update from "immutability-helper";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import ImageBrowserDialog from "../Dialogs/ImageBrowserDialog";
import {blobsRootFolderName, retrieveBlobImages} from "../../apis/blobImageApi";
import {ImageGallery} from "../../Interfaces/ImageGallery";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {IWelcomeMessage} from "../../Interfaces/IWelcomeMessage";
import WelcomeCardApi from "../../apis/welcomeCardApi";
import {withRouter, WithRouterProps} from "../../services/TelemetryService/withRouter.hook";

/**
 * Welcome props
 */
interface IWelcomeProps extends WithRouterProps {
}

/**
 * Default Summary
 */
const defaultSummary = "Welcome to Company Communicator Pro.\n\nI am here to inform you about your company news.\n\n" +
    "You will soon receive all important news updates from me. If you want, you can change you favorite " +
    "communication language and the topics that interest you by clicking on the button below.";

/**
 * Default welcome message
 */
const defaultWelcomeMessage: IWelcomeMessage = {
    title: "Welcome",
    summary: defaultSummary,
    imageLink: "",
    tenantId: "",
};

/**
 * Welcome state
 */
interface IWelcomeState {
    loaded: boolean,
    oldWelcomeMessage: IWelcomeMessage,
    welcomeMessage: IWelcomeMessage,
    availableImages: Array<ImageGallery>,
    showImageBrowser: boolean,
    showConfirmCloseDialog: boolean,
    updatingDatabase: boolean,
    isSaving: boolean,
}

/**
 * Welcome component
 */
class Welcome extends React.Component<IWelcomeProps, IWelcomeState> {

    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    // Adaptive card
    private readonly card: any;

    /**
     * Constructor
     */
    constructor(props: IWelcomeProps, context: IAppContextState) {
        super(props);
        this.card = getInitAdaptiveCard();

        defaultWelcomeMessage.tenantId = context.teams.tenantId;

        this.state = {
            oldWelcomeMessage: defaultWelcomeMessage,
            welcomeMessage: defaultWelcomeMessage,
            loaded: false,
            availableImages: new Array<ImageGallery>(),
            showImageBrowser: false,
            showConfirmCloseDialog: false,
            updatingDatabase: false,
            isSaving: false,
        }
    }

    /**
     * Component initialization
     */
    async componentDidMount() {
        await this.retrieveImages();
        await this.retrieveWelcomeMessage();
        this.renderCard();
    }

    /**
     * When component has updated
     * @param prevProps previous props
     * @param prevState previous state
     */
    componentDidUpdate(prevProps: Readonly<IWelcomeProps>, prevState: Readonly<IWelcomeState>) {
        if (this.state.loaded && (
            prevState.welcomeMessage.title !== this.state.welcomeMessage.title ||
            prevState.welcomeMessage.summary !== this.state.welcomeMessage.summary ||
            prevState.welcomeMessage.imageLink !== this.state.welcomeMessage.imageLink))
            this.renderCard();
    }

    /**
     * Retrieve saved welcome message
     */
    retrieveWelcomeMessage = async () => {
        let welcomeCard: IWelcomeMessage | undefined = await WelcomeCardApi.getWelcomeCard();
        if (!welcomeCard) {
            welcomeCard = update(this.state.welcomeMessage, {
                tenantId: {$set: this.context.teams.tenantId ?? ""},
            })
            welcomeCard = await WelcomeCardApi.createWelcomeCard(welcomeCard);
        }
        if (!!welcomeCard.imageLink) welcomeCard.imageLink = blobsRootFolderName + welcomeCard.imageLink;
        this.setState({loaded: true, oldWelcomeMessage: welcomeCard, welcomeMessage: welcomeCard})
    }

    /**
     * Retrieve available images
     */
    retrieveImages = (): Promise<void> => {
        return new Promise<void>((resolve) => {
            retrieveBlobImages().then(images => {
                this.setState({availableImages: images}, resolve);
            })
        })
    }

    /**
     * On close
     */
    onClose = () => {
        if (!this.isMessageEdited()) this.props.navigate("/messages");
        else this.setState({showConfirmCloseDialog: true});
    }

    /**
     * When user has selected an image
     */
    handleSelectImage = (image: ImageGallery) => {
        this.setState({
            welcomeMessage: update(this.state.welcomeMessage, {imageLink: {$set: image.imageLink}}),
            showImageBrowser: false
        });
    }

    /**
     * When the message has changed
     * @param message message
     */
    onMessageChange = (message: string) => {
        this.setState({
            welcomeMessage: update(this.state.welcomeMessage, {summary: {$set: message}})
        }, () => this.renderCard());
    }

    /**
     * Update available images
     * @param images images
     */
    handleImagesUpdate = (images: Array<ImageGallery>) => {
        return new Promise<void>(resolve => {
            this.setState({availableImages: images}, resolve);
        })
    }

    /**
     * Check if message has been edited
     */
    isMessageEdited = (): boolean => {
        return this.state.oldWelcomeMessage.title !== this.state.welcomeMessage.title ||
            this.state.oldWelcomeMessage.summary !== this.state.welcomeMessage.summary ||
            this.state.oldWelcomeMessage.imageLink !== this.state.welcomeMessage.imageLink;
    }

    onSaveCard = async () => {
        let imageLink = this.state.welcomeMessage.imageLink.replace(blobsRootFolderName, "");
        let welcomeCard: IWelcomeMessage = update(this.state.welcomeMessage, {
            tenantId: {$set: this.context.teams.tenantId as string},
            imageLink: {$set: imageLink}
        });
        this.setState({isSaving: true});
        if (welcomeCard.tenantId) {
            await WelcomeCardApi.updateWelcomeCard(welcomeCard);
        } else {
            await WelcomeCardApi.createWelcomeCard(welcomeCard)
        }
        this.props.navigate("/messages")
    }

    /**
     * Render component
     */
    render() {
        return (
            <Flex column fill gap="gap.medium" className={"p-20px"}>
                {this.renderNavBar()}
                {!this.state.loaded ?
                    <Loader styles={{width: "100%", height: "100%"}}/>
                    :
                    <Flex className="form_layout" gap="gap.large" styles={{marginTop: "10px"}}>
                        <FlexItem styles={{maxWidth: "calc(100% - 530px)"}}>
                            {this.renderForm()}
                        </FlexItem>
                        <FlexItem styles={{minWidth: "500px", maxWidth: "500px"}}>
                            <div className="welcomeAdaptiveCardContainer"/>
                        </FlexItem>
                    </Flex>
                }
                {this.renderImageBrowser()}
                {this.renderConfirmCloseDialog()}
            </Flex>
        );
    }

    /**
     * Render navbar
     */
    renderNavBar = () => {
        return (<>
                <Flex gap={"gap.small"}>
                    <Flex gap={"gap.small"} vAlign={"center"}>
                        <MegaphoneIcon size={"large"} styles={{color: "#782753"}}/>
                        <Header as={"h2"} styles={{margin: "0"}} content={languageObject.translate("WelcomeMessage")}/>
                    </Flex>
                    <FlexItem push>
                        <Button
                            content={languageObject.translate("Save")}
                            onClick={() => this.onSaveCard()}
                            primary
                            icon={<SaveIcon outline/>}
                            loading={this.state.isSaving}
                            disabled={this.state.isSaving || !this.isMessageEdited()}/>
                    </FlexItem>
                    <Button
                        icon={<CloseIcon outline/>}
                        content={languageObject.translate("CloseText")}
                        onClick={this.onClose}/>
                </Flex>
                <Flex>
                    <Alert warning content={languageObject.translate("MessageOrgOnly")}/>
                </Flex>
            </>
        )
    }

    /**
     * Render form
     */
    renderForm = () => {
        return (
            <Flex column className={"formContainerWelcomeMessage"}>
                {/* Title input */}
                <EditInput
                    required
                    inverted
                    max={200}
                    label={languageObject.translate("Title")}
                    value={this.state.welcomeMessage.title ?? ""}
                    setValue={title => this.setState({welcomeMessage: update(this.state.welcomeMessage, {title: {$set: title}})})}
                />
                {/* Image input */}
                <Flex gap={"gap.small"} vAlign={"end"}>
                    <EditInput
                        inverted
                        disabled
                        label={languageObject.translate("Image")}
                        value={this.state.welcomeMessage.imageLink?.replace(blobsRootFolderName, "/") ?? ""}/>
                    <Button
                        content={languageObject.translate("Browse")}
                        icon={<ImageLibraryIcon outline/>}
                        onClick={() => this.setState({showImageBrowser: true})}/>
                </Flex>
                {/* Message input */}
                <RichTextEditor
                    max={1000}
                    id={"welcome_richtext_editor"}
                    value={this.state.welcomeMessage.summary}
                    label={languageObject.translate("Message")}
                    inputHeight={200}
                    onChange={this.onMessageChange}
                />
            </Flex>
        )
    }

    /**
     * Render image browser
     */
    renderImageBrowser = () => {
        const image = this.state.availableImages.find(img => img.imageLink === this.state.welcomeMessage.imageLink);
        return (
            <ImageBrowserDialog
                show={this.state.showImageBrowser}
                selectedImage={image}
                availableImages={this.state.availableImages}
                handleSelectImage={image => this.handleSelectImage(image)}
                onHide={() => this.setState({showImageBrowser: false})}
                onImagesUpdated={this.handleImagesUpdate}
            />
        )
    }

    /**
     * Render confirm close dialog
     */
    renderConfirmCloseDialog = () => {
        return (
            <ConfirmDialog
                show={this.state.showConfirmCloseDialog}
                onConfirm={() => this.props.navigate("/messages")}
                onCancel={() => this.setState({showConfirmCloseDialog: false})}
                title={languageObject.translate("UnsavedChanges")}
                content={languageObject.translate("DiscardEdits")}/>
        )
    }

    /**
     * Render adaptive card
     */
    renderCard = () => {
        const image = this.state.availableImages.find(img => img.imageLink === this.state.welcomeMessage.imageLink);
        updateWelcomeCard(this.card,
            this.state.welcomeMessage.title,
            image?.content ?? "",
            this.state.welcomeMessage.summary);
    }
}

export default withRouter(Welcome)